import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { parseISO, format } from "date-fns";

import { Videoplayer } from "@openabos/react-circular-player";
import "@openabos/react-circular-player/dist/cjs/index.css";
import "../../styles/react-circular-player-new-styles.css";

import { Done, DoneAll, AccessTime, FormatListBulletedOutlined, GetApp } from "@material-ui/icons";
import { Dialog, DialogTitle, Tooltip, Button, Divider } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import saveFile from "../../utils/saveFile";
import Audio from "../Audio";
import EventPreviewQuotedMessage from "../EventPreviewQuotedMessage";
import LocationPreview from "../LocationPreview";
import MarkdownWrapper from "../MarkdownWrapper";
import MessageImage from "../MessageImage";
import MessageUtils from "../../utils/classes/MessageUtils";
import MultiVcardPreview from "../MultiVcardPreview";
import PixMessage from "../PixMessage";
import PollPreviewQuotedMessage from "../PollPreviewQuotedMessage";
import VcardPreview from "../VcardPreview";
import ViewOnceMessageWeb from "../ViewOnceMessageWeb";
import ViewOnceRepliedMessage from "../ViewOnceRepliedMessage";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import TemplateMessageUtils from "../../utils/classes/TemplateMessageUtils";

const useStyles = makeStyles(theme => ({
  // Main Containers
  dialogContent: {
    gap: "0px",
    padding: "1.5em",
    background: theme.backgroundImage,
    overflowX: "auto",
  },

  // Quoted Message
  buttonListFooter: {
    fontSize: "12px",
    color: theme.palette.text.primary,
  },

  stickerMessage: {
    maxHeight: 225,
    maxWidth: 225,
    borderRadius: 5,
    transition: "all 0.3s",
  
    [theme.breakpoints.down("sm")]: {
      maxHeight: 150,
      maxWidth: 150,
    },
  },

  deletedStickerMessage: {
    opacity: 0.7,
    filter: "grayscale(80%)",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  messageMediaFullScreen: {
    transition: "all 0.30s",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.background.quotedPaper,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  // Arrow
  "@keyframes moveUpDown": {
    "0%": { 
      opacity: "100%",
      transform: "translateY(0px)",
    },
  
    "50%": { 
      opacity: "65%",
      transform: "translateY(5px)",
    },
  
    "100%": { 
      opacity: "100%", 
      transform: "translateY(0px)",
    },
  },

  arrowContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "3px",
  },

  arrow: {
    fontSize: "2em",
    cursor: "default",
    animation: "$moveUpDown 1.5s infinite",
  },


  // Current Message View
  currentMessageViewContainer: { overflowX: "auto", },

  messageView: {
    overflowX: "auto",
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "auto",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    marginRight: "1em",
    color: theme.palette.text.primary,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  messageFromContact: {
    backgroundColor: theme.palette.background.paper,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 8,
  },

  messageFromMe: {
    backgroundColor: theme.palette.primary.messageBox,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.text.primary,
    userSelect: "none",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackDoneAllIcon: {
    color: theme.palette.secondary.ackCheck,
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  reactionListSticker: {
    position: "absolute",
    bottom: "-20px",
    left: "5px",
    display: "flex",
    flexDirection: "row",
    zIndex: "9",
    gap: "2px",
  },

  reactionSpan: {
    borderRadius: "20px",
    padding: "3px 3px 3px 3px",
    backgroundColor: theme.palette.background.paper,
    fontSize: "15px",
    userSelect: "none",
    "&:hover": { filter: "brightness(0.92)", },
  },

  // Edit Message Field
  editMessageFieldContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    gap: "1em",
    paddingBottom: "1em",
  },

  sendMessageIcons: {
    borderRadius:"20px",
    color: "grey",
  },

  //  ***********************
  //  ** Internal Messages **
  //  ***********************
  internalMessage: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "auto",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      visibility: "visible",
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
  
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.internalMessage,
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)" },
  },
}));

const MessageEditHistoryModal = ({ open, onClose, message }) => {
  //  ***********
  //  ** Datas **
  //  ***********
  const classes = useStyles();
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  let messageContainerClasses = {};
  if (message && message !== undefined) {
    const isMessageInternal = message.isInternal
      || [DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.transferenceObservation].includes(message.mediaType);

    if (!message.fromMe) messageContainerClasses = `${classes.messageView} ${classes.messageFromContact}`;
    else if (!isMessageInternal) messageContainerClasses = `${classes.messageView} ${classes.messageFromMe}`;
    else messageContainerClasses = `${classes.internalMessage} ${classes.messageFromMe}`;
  }



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    const handleFullScreen = () => setIsFullscreen(!!document.fullscreenElement);

    document.addEventListener("fullscreenchange", handleFullScreen);
    return () => document.removeEventListener("fullscreenchange", handleFullScreen);
  }, []);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location && message.body.split("|").length >= 2) {
      const { imageLocation, linkLocation, descriptionLocation } = MessageUtils.extractLocationInfo(message);
      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce) {
      return (<ViewOnceMessageWeb message={message} />)
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button) { 
      const { buttonTitle, buttonFooter, buttonBody, buttonOptionsArray } = MessageUtils.extractButtonInfo(message);

      return(
        <>
          <b>{buttonTitle}</b>
          <p>{buttonBody}</p>
          <i className={classes.buttonListFooter}>{buttonFooter}</i>
          <hr />
          <center>
            {buttonOptionsArray.map((option, i) => (
              <Button
                color="inherit"
                variant="outlined"
                target="_blank"
                disabled
              >
                {option}
              </Button>
            ))}
          </center>
        </>
      );

    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list) { 
      const { listTitle, listBody, listOption, listFooter, listOptionsArray } = MessageUtils.extractListInfo(message);
      return (
        <>
          <b>{listTitle}</b>
          <p>{listBody}</p>
          <i className={classes.buttonListFooter}>{listFooter}</i>
          <center>
            <Button
              startIcon={<FormatListBulletedOutlined />}
              color="inherit"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
              disabled
            >
              {listOption}
            </Button>
          </center>
          
          <hr />

          <center>
            {listOptionsArray.map((option, i) => (
              <Button
                color="inherit"
                variant="outlined"
                target="_blank"
                href={message.mediaUrl}
                disabled
              >
                {option.split("%%")[0]}
              </Button>
            ))}
          </center>
        </>
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard) {
      const { contact, number } = MessageUtils.extractVcardInfo(message);
      return <VcardPreview contact={contact} number={number} enableHandleNewChat={false} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard) {
      const { contactsList } = MessageUtils.extractMultivcardInfo(message);
      return <MultiVcardPreview contacts={contactsList} enableStartChat={false} />;
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image && !message.isViewOnce) {
      return <MessageImage message={message} ticketId={message.ticketId} replyMessage={true} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image && message.isViewOnce) {
      return <ViewOnceRepliedMessage isPhoto={true} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker) {
      return (
        <img
          className={clsx(classes.stickerMessage, {[classes.deletedStickerMessage]: message.isDeleted,})}
          src={message.mediaUrl} alt={message.body} width={225} height={225}
        />
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio) {
      return <Audio url={message.mediaUrl} isVoice={message.isVoice} isFromMe={message.fromMe} />
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv) {
      return (
        <Videoplayer size={200} src={message.mediaUrl} boundaryColor={"#ff6961"} />
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && !message.isViewOnce) {
      let mediaClass = isFullscreen ? `${classes.messageMediaFullScreen}` : `${classes.messageMedia}`;

      return (
        <>
        <video
          className={mediaClass}
          src={message.mediaUrl}
          controls={!message.isGif}
          autoPlay={message.isGif}
          muted={message.isGif}
          loop={message.isGif}
        />
        <br />
        </>
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video && message.isViewOnce) {
      return (
        <>
        <ViewOnceRepliedMessage isPhoto={false} />
        <br />
        </>
      );
    }
    
    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation) {
      return <PollPreviewQuotedMessage message={message} />
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event) {
      return <EventPreviewQuotedMessage message={message} />;
    }

    else if (message.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix) {
      return <PixMessage message={message} isQuoted={false} />
    }
    
    else {
      const handleClick = (event) => {
        event.preventDefault();
        saveFile(message);
      }

      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="inherit"
              variant="outlined"
              target="_blank"
              href="#"
              onClick={handleClick}
              className={classes.floatingButton}
            >
              {i18n.t("messagesInput.download")}
            </Button>
            
          </div>
          <Divider />
        </>
      );
    }
  };

  const validateCheckMessageMediaForQuotedMessage = (message) => {
    if (!message.quotedMsg) return <></>;

    const shouldCheckMessageMedia = message.quotedMsg.mediaUrl
      || [
        DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pix
        , DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.viewOnce
      ].includes(message.quotedMsg.mediaType);

    if (shouldCheckMessageMedia) return checkMessageMedia(message.quotedMsg);
    else return <></>;
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, { [classes.quotedContainerRight]: message.fromMe, })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, { [classes.quotedSideColorRight]: message.quotedMsg?.fromMe, })}
        />

        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>{message.quotedMsg?.contact?.name}</span>
          )}

          {message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list 
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.vcard 
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.multiVcard
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.sticker
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.audio 
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video 
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.application
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.pollCreation
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.event
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.ptv
            && message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template
            && (
              <MarkdownWrapper>{message.quotedMsg?.body}</MarkdownWrapper>
            )
          }

          {message.quotedMsg?.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template && (
            TemplateMessageUtils.renderTemplate(message.quotedMsg, null, false)
          )}

          {message.quotedMsg?.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.button && (
            <>
              <b>{message.quotedMsg?.body.split("##")[1]}</b>
              <p>{message.quotedMsg?.body.split("||")[0]}</p>
              <i className={classes.buttonListFooter}>{message.quotedMsg?.body.split("##")[2]}</i>
            </>
          )}

          {message.quotedMsg?.mediaType === DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.list && (
            <>
              <b>{message.quotedMsg?.body.split("||")[0].split("%%")[0]}</b>
              <p>{message.quotedMsg?.body.split("||")[0].split("%%")[1]}</p>
              <i className={classes.buttonListFooter}>{message.quotedMsg?.body.split("||")[0].split("%%")[3]}</i>
            </>
          )}

          {message.quotedMsg?.mediaType !== DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.template && (
            validateCheckMessageMediaForQuotedMessage(message)
          )}
        </div>
      </div>
    );
  };

  const renderMessageAck = (message) => {
    if (!message.fromMe) return <DoneAll fontSize="small" className={classes.ackIcons} />;
    if ([0, 9].includes(message.ack)) return <AccessTime fontSize="small" className={classes.ackIcons} />;
    if ([1, 2].includes(message.ack)) return <Done fontSize="small" className={classes.ackIcons} />;
    if ([3].includes(message.ack)) return <DoneAll fontSize="small" className={classes.ackIcons} />;
    if ([4, 5].includes(message.ack)) return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
  };




  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          <span>{i18n.t("editMessageHistoryModal.title.up")}</span>
        </DialogTitle>

        <div className={classes.dialogContent}>
          {message && (
            <>
            {/* 
              *******************
              ** Previous Body **
              *******************
            */}
            <div className={classes.currentMessageViewContainer}>
              <div className={messageContainerClasses}>
                <div className={classes.textContentItem}>
                  {message.quotedMsg && (
                    <div className={classes.textContentItem}>{renderQuotedMessage(message)}</div>
                  )}

                  {message.mediaUrl && checkMessageMedia(message)}

                  {/* 
                    ***---- Message Body ----***
                  */}
                  <MarkdownWrapper>{message.previousBody}</MarkdownWrapper>

                  {/* 
                    ***---- Time Stamp and ACK ----***
                  */}
                  <span className={classes.timestamp}>
                    {JSON.stringify(message) !== "{}" && format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>

                  {/* 
                    ***---- Reactions ----***
                  */}
                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionListSticker}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.contactReaction}</span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.fromMeReaction}</span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            


            {/* 
              ***********
              ** Arrow **
              ***********
            */}
            <div className={classes.arrowContainer}>
              <span className={classes.arrow}>⬇️</span>
            </div>

            {/* 
              ******************
              ** Current Body **
              ******************
            */}
            <div className={classes.currentMessageViewContainer}>
              <div className={messageContainerClasses}>
                <div className={classes.textContentItem}>
                  {message.quotedMsg && (
                    <div className={classes.textContentItem}>{renderQuotedMessage(message)}</div>
                  )}

                  {message.mediaUrl && checkMessageMedia(message)}

                  {/* 
                    ***---- Message Body ----***
                  */}
                  <MarkdownWrapper>{message.body}</MarkdownWrapper>

                  {/* 
                    ***---- Time Stamp and ACK ----***
                  */}
                  <span className={classes.timestamp}>
                    {JSON.stringify(message) !== "{}" && format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>

                  {/* 
                    ***---- Reactions ----***
                  */}
                  {(message.contactReaction || message.fromMeReaction) && (
                    <div className={classes.reactionListSticker}>
                      {message.contactReaction && (
                        <Tooltip title={i18n.t("reactions.fromContact")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.contactReaction}</span>
                        </Tooltip>
                      )}

                      {message.fromMeReaction && (
                        <Tooltip title={i18n.t("reactions.fromMe")} placement="bottom-start" arrow>
                          <span className={classes.reactionSpan}>{message.fromMeReaction}</span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default MessageEditHistoryModal;